// ---------------------------------------------------------------------------
// BUG #36/#39/#32: client-side mirror of
// backend/imports/api/util/programmeKeys.ts — the pure period-key arithmetic
// the server's programme guard is built on. Any change here must be made in
// lockstep with that file, and vice versa: the member pages must derive the
// same offset and the same locked set as the server from the same serialised
// data, or the page offers an edit the server quietly reverts (BUG #36's
// dead-end). Duplicated rather than imported because the frontend cannot
// import backend modules; unit-tested in programmeKeys.test.ts.
//
// Shared by ContractProgramme.tsx and ContractReport.tsx — the #32 regression
// was precisely these two pages disagreeing about which month an allocation
// key belongs to.
// ---------------------------------------------------------------------------

const PERIOD_PATTERN = /^(\d{4})-(\d{2})$/

// months since year 0 for a `YYYY-MM` period key, or null if it is not one
export function periodMonthIndex(period: string): number | null {
  const match = PERIOD_PATTERN.exec(period)
  if (match === null) return null
  const year = Number(match[1])
  const month = Number(match[2])
  if (month < 1 || month > 12) return null
  return year * 12 + (month - 1)
}

export function shiftPeriod(period: string, months: number): string | null {
  const index = periodMonthIndex(period)
  if (index === null) return null
  const shifted = index + months
  if (shifted < 0) return null
  const year = Math.floor(shifted / 12)
  const month = (shifted % 12) + 1
  return `${String(year).padStart(4, '0')}-${String(month).padStart(2, '0')}`
}

// #53: the last work month of a contract, read from its stored endDate.
// Contract dates entered in UK local time during BST are stored as 23:00Z of
// the previous day, so a raw UTC read sees the day (and sometimes the month)
// before the one the user entered; nudge +2h past midnight before reading the
// month — the same read the server's atLocalMidnight() performs for the
// period generators, so the discriminator below sees the same end month they
// do. Mirror of contractEndMonth in the backend util.
export function contractEndMonth(endDate: Date | string): string {
  const date = new Date(new Date(endDate).getTime() + 2 * 60 * 60 * 1000)
  const year = date.getFullYear().toString().padStart(4, '0')
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  return `${year}-${month}`
}

// this contract's stored programme-key → work-month offset. A programme saved
// before #32 (b069cb1) has keys one month ahead of the work they plan; derive
// the offset from the stored data itself, exactly as the server guard does.
// Any gap other than 0 or 1 is a shape we do not understand and reads as 0
// (exact-key matching only).
//
// #53: a head gap of 1 alone no longer proves due-keying. Now that
// contractProgrammePeriods() starts at the first work month, a programme
// saved under the pre-#53 generator (b069cb1's "15 contracts still start one
// month late") is WORK-keyed yet missing its head month — head gap 1, but the
// right offset is 0. The tail tells the two shapes apart, verified against
// every stored programme on the production-shape data: the legacy due-keyed
// generator always ran one month PAST the contract end (max key == end month
// + 1), while every post-b069cb1 save ends AT the end month. So offset 1 now
// requires the head gap AND the legacy tail. When the caller cannot supply
// the end month (or it does not parse), fall back to the head-gap-only read —
// the pre-#53 behaviour.
export function programmeKeyOffset(
  storedKeys: string[],
  firstReportPeriod: string | undefined,
  endMonth: string | undefined
): number {
  let offset = 0
  const indices = storedKeys
    .map(periodMonthIndex)
    .filter((i): i is number => i !== null)
  const firstWorkIndex =
    firstReportPeriod === undefined ? null : periodMonthIndex(firstReportPeriod)
  if (indices.length > 0 && firstWorkIndex !== null) {
    const candidate = Math.min(...indices) - firstWorkIndex
    if (candidate === 1) {
      const endIndex = endMonth === undefined ? null : periodMonthIndex(endMonth)
      if (endIndex === null || Math.max(...indices) === endIndex + 1) offset = 1
    }
  }
  return offset
}

// #32 REGRESSION: the month a contractor should see for an allocation key —
// the month the work is done. Inverse of planKeyForWorkMonth. offset 0 is the
// identity, so work-month-keyed programmes are untouched.
export function workMonthForKey(key: string, offset: number): string {
  if (offset === 0) return key
  return shiftPeriod(key, -offset) ?? key
}

// the allocation key holding the plan for a given work month
export function planKeyForWorkMonth(workMonth: string, offset: number): string {
  if (offset === 0) return workMonth
  return shiftPeriod(workMonth, offset) ?? workMonth
}
